import { create } from 'zustand';

const API_URL = '/api';

interface User {
  id: string;
  name: string;
  email: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,

  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || error.message || 'Login fehlgeschlagen');
      }

      const data = await response.json();
      // Backend returns accessToken (not token)
      const token = data.accessToken;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', data.refreshToken || '');
      const user: User = {
        id: data.user?.id || '',
        name: data.user?.firstName
          ? `${data.user.firstName} ${data.user.lastName || ''}`.trim()
          : (data.user?.email || 'Nutzer'),
        email: data.user?.email || email,
      };
      set({ user, token, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  register: async (name: string, email: string, password: string) => {
    set({ isLoading: true });
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || error.message || 'Registrierung fehlgeschlagen');
      }

      const data = await response.json();
      const token = data.accessToken;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', data.refreshToken || '');
      const user: User = {
        id: data.user?.id || '',
        name: data.user?.firstName
          ? `${data.user.firstName} ${data.user.lastName || ''}`.trim()
          : (data.user?.email || 'Nutzer'),
        email: data.user?.email || email,
      };
      set({ user, token, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    set({ user: null, token: null, isAuthenticated: false });
  },

  initializeAuth: () => {
    const token = localStorage.getItem('token');
    if (token && token !== 'undefined') {
      try {
        const base64 = token.split('.')[1];
        const json = atob(base64);
        const payload = JSON.parse(json);
        const userId = payload.userId || payload.sub || '';
        set({
          token,
          isAuthenticated: true,
          user: { id: userId, name: 'Nutzer', email: '' },
        });
      } catch {
        localStorage.removeItem('token');
        set({ token: null, isAuthenticated: false, user: null });
      }
    } else {
      // Clear any leftover "undefined" string
      if (token === 'undefined') localStorage.removeItem('token');
      set({ token: null, isAuthenticated: false });
    }
  },
}));
